import { usePrevious } from '@uidotdev/usehooks';
import { ColorNordicGrey4, ColorSystemNegative3, Spacing8 } from 'autogen/design-tokens/tokens';
import type { BasketLineViewModel } from 'autogen/swagger/all';
import WithIcon from 'components/hoc/withIcon/WithIcon';
import Icon from 'components/Icon';
import Flex from 'components/Layout/components/Flex';
import SubscriptionNudge from 'components/subscriptionNudge/SubscriptionNudge';
import usePointOfSaleRequest from 'hooks/user/usePointOfSaleRequest';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';
import FavoriteItemButton from './components/FavoriteItemButton';
import GiveAsGiftInput from './components/GiveAsGiftInput';
import NumberInput from './components/NumberInput';
import QuantitySelect, { QuantityText } from './components/QuantitySelect';
import RemoveItemButton from './components/RemoveItemButton';
import SubscriptionFrequencySelect from './components/SubscriptionFrequencySelect';

/* #region styles */
const StyledGiveasGiftInput = styled(GiveAsGiftInput)`
    flex-basis: 100%;

    ${breakpoint.up(sizes.sm)} {
        flex-basis: auto;
    }
`;

const StyledFlexRow = styled(Flex.Row)`
    flex-wrap: wrap;
`;

const StyledQuantitySelect = styled(QuantitySelect)`
    flex-grow: 1;

    ${breakpoint.up(sizes.lg)} {
        flex-grow: 0;
    }
`;

const PrimaryActionContainer = styled(Flex.Row)`
    width: 100%;

    ${breakpoint.up(sizes.sm)} {
        width: auto;
        margin-left: auto;
    }
`;

const StyledSubscriptionFrequencySelect = styled(SubscriptionFrequencySelect)`
    && {
        border: none;
        background-color: ${ColorNordicGrey4};
    }
`;
/* #endregion */

export default function Actions({ model }: { model: BasketLineViewModel }) {
    const {
        isReadOnly,
        productId,
        quantity,
        maximumSalesQuantity,
        minimumSalesQuantity,
        subscriptionNudge,
        subscriptionInfo,
        hasValidationErrors
    } = model;
    const { isPosRequest } = usePointOfSaleRequest();
    const previousQuantity = usePrevious(quantity);
    const autoFocusNumberInput = !!previousQuantity && quantity > 9; // prevent autofocus on initial render

    if (isReadOnly || isPosRequest) {
        return (
            <Flex.Row gap={Spacing8}>
                <QuantityText quantity={quantity} />
            </Flex.Row>
        );
    }

    const quantityInput =
        quantity > 9 ? (
            <NumberInput
                productId={productId}
                quantity={quantity}
                maximumSalesQuantity={maximumSalesQuantity}
                minimumSalesQuantity={minimumSalesQuantity}
                autoFocus={autoFocusNumberInput}
            />
        ) : (
            <StyledQuantitySelect
                productId={productId}
                quantity={quantity}
                maximumSalesQuantity={maximumSalesQuantity}
                minimumSalesQuantity={minimumSalesQuantity}
            />
        );

    return (
        <Flex.Column>
            {subscriptionNudge && <SubscriptionNudge model={subscriptionNudge} />}
            <StyledFlexRow gap={Spacing8} justifyContent={{ lg: 'space-between' }}>
                <StyledGiveasGiftInput model={model} />
                {subscriptionInfo && !subscriptionNudge && (
                    <StyledSubscriptionFrequencySelect model={subscriptionInfo.subscriptionFrequencyInfo} />
                )}

                <PrimaryActionContainer gap={Spacing8}>
                    {hasValidationErrors ? (
                        <WithIcon icon={<Icon type="Alert" size={24} fill={ColorSystemNegative3} />}>
                            {quantityInput}
                        </WithIcon>
                    ) : (
                        quantityInput
                    )}

                    <RemoveItemButton productId={productId} />
                    <FavoriteItemButton
                        productId={productId}
                        initialFavoriteState={model.favoriteStatus.favoriteStatus === 'IsFavorite'}
                    />
                </PrimaryActionContainer>
            </StyledFlexRow>
        </Flex.Column>
    );
}
